/**
 * 사각형 목록 → 다음 포커스 (v10, `docs/25` §3.3).
 *
 * **DOM 순서가 아니라 기하로 정한다.** `.controls` 는 이미 둘이 어긋나 있다 —
 * `.controls__button--skip { margin-left: auto }` 가 넷째 버튼을 오른쪽 끝으로 밀고,
 * `flex-wrap: wrap` 이 좁은 화면에서 그것을 아랫줄로 떨어뜨린다(v9 C9 가 폰 폭 실기로
 * 넣었다). 인덱스로 움직이면 **눈에 보이는 것과 다르게 움직이고**, TV 리모컨에서 그보다
 * 나쁜 실패가 없다.
 *
 * **`Rect` 가 `DOMRect` 가 아닌 것이 이 모듈의 뼈대다.** 사각형을 손으로 만들 수 있어야
 * 기하 판정 전체가 node 테스트에 덮인다 — jsdom 에는 레이아웃이 없어
 * `getBoundingClientRect()` 가 전부 `{0,0,0,0}` 이고, 그 위에서 쓸 수 있는 것은 **우리가
 * 출시하지 않는 모델**뿐이다 (`docs/25` D7).
 */

import type { Direction } from './keys.js';

/** 뷰포트 좌표계의 사각형. `getBoundingClientRect()` 의 네 값만 쓴다. */
export interface Rect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * 포커스 후보 하나.
 *
 * `index` 는 **DOM 순서**다. 어댑터가 그 순서로 매기고, 되돌려 받은 값으로 요소를 찾는다.
 * 동점을 가르는 기준이자 `entryFocusIndex` 의 기준이기도 하다.
 */
export interface FocusCandidate {
  readonly index: number;
  readonly rect: Rect;
}

/**
 * 방향축을 통과했는지 볼 때의 여유. **부동소수 반올림만 흡수한다.**
 *
 * 크게 잡으면 나란히 붙은 버튼들이 서로 「오른쪽」이 되어 판정이 흔들린다.
 */
const TOLERANCE = 1;

/**
 * 교차축이 겹치지 않을 때의 벌점 배수.
 *
 * **겹침을 요구하지 않고 선호만 한다.** 요구하면 `.controls` 에서 「오른쪽 화살표가 아무
 * 일도 안 한다」가 생기는데, 버튼이 넷뿐인 화면에서 **아무 일도 안 하는 것이 어딘가
 * 합리적인 곳으로 가는 것보다 나쁘다.** 이 배수 때문에 컨트롤 바 어디서든 ↑ 가 우상단
 * 설정 버튼으로 가고(가로로 멀지만 유일), 접힌 줄에서 → 가 아랫줄로 간다.
 */
const ACROSS_PENALTY = 4;

/**
 * 그려지지 않은 후보를 뺀다.
 *
 * **넓이나 높이가 0 이면 화면에 없다.** 그리로 포커스가 가면 **링이 사라지고**, 시청자에게
 * 그것은 「리모컨이 죽었다」와 구분되지 않는다.
 */
function isDrawn(candidate: FocusCandidate): boolean {
  const { left, top, right, bottom } = candidate.rect;
  return right - left > 0 && bottom - top > 0;
}

/** 진행축·교차축의 구간. 방향에 따라 x·y 가 뒤바뀔 뿐 규칙은 하나다. */
function axes(rect: Rect, direction: Direction) {
  const horizontal = direction === 'left' || direction === 'right';
  return horizontal
    ? { near: rect.left, far: rect.right, crossLow: rect.top, crossHigh: rect.bottom }
    : { near: rect.top, far: rect.bottom, crossLow: rect.left, crossHigh: rect.right };
}

/** 그 방향으로 넘어가 있는가. 같은 줄에 나란히 있는 것은 통과하지 못한다. */
function isAhead(from: Rect, to: Rect, direction: Direction): boolean {
  const a = axes(from, direction);
  const b = axes(to, direction);
  return direction === 'right' || direction === 'down'
    ? b.near >= a.far - TOLERANCE
    : b.far <= a.near + TOLERANCE;
}

/** 가까울수록 낮다. 교차축이 겹치면 진행축 거리만 본다. */
function score(from: Rect, to: Rect, direction: Direction): number {
  const a = axes(from, direction);
  const b = axes(to, direction);

  const along =
    direction === 'right' || direction === 'down'
      ? Math.max(0, b.near - a.far)
      : Math.max(0, a.near - b.far);

  const overlap = Math.min(a.crossHigh, b.crossHigh) - Math.max(a.crossLow, b.crossLow);
  const across = overlap > 0 ? 0 : Math.max(a.crossLow - b.crossHigh, b.crossLow - a.crossHigh);

  return along + across * ACROSS_PENALTY;
}

/**
 * 그 방향의 다음 후보. **없으면 `null` 이고, 감싸지 않는다.**
 *
 * 가장자리에서 반대편으로 돌아가지 않는 이유: 리모컨에서 그것은 「한 칸 갔다」가 아니라
 * 「화면 반대편으로 튀었다」로 보인다. 그리고 `null` 은 호출부에서 **기본 동작을 막지
 * 않는 근거**가 된다 — 문서형 화면의 키보드 스크롤이 거기 걸려 있다 (`decide.ts`).
 *
 * 동점은 **작은 `index`(DOM 순서)** 가 이긴다. 이것이 없으면 완전히 겹친 두 후보에서
 * 결과가 실행마다 달라지고, **하네스가 간헐 실패한다** — 사람이 「재실행하면 되네」로
 * 넘기는, 가장 나쁜 종류의 실패다.
 */
export function nextFocusIndex(
  from: FocusCandidate,
  candidates: readonly FocusCandidate[],
  direction: Direction,
): number | null {
  let best: { index: number; score: number } | null = null;

  for (const candidate of candidates) {
    // **자기 자신을 뺀다.** 점수가 0 이라 두면 언제나 자기가 이겨 리모컨이 멈춘다.
    if (candidate.index === from.index) continue;
    if (!isDrawn(candidate)) continue;
    if (!isAhead(from.rect, candidate.rect, direction)) continue;

    const value = score(from.rect, candidate.rect, direction);
    if (
      best === null ||
      value < best.score ||
      (value === best.score && candidate.index < best.index)
    )
      best = { index: candidate.index, score: value };
  }

  return best === null ? null : best.index;
}

/**
 * 층에 처음 들어갈 때의 착지점. **기하가 아니라 DOM 순서다.**
 *
 * 기하(최상단-최좌측)로 하면 시청 화면에서 `.stage__settings`(우상단)가 컨트롤 바(하단)를
 * 이겨서 **재생을 시작하고 처음 누른 화살표가 「설정」에 착지한다.** DOM 순서는
 * `ControlBar` 가 설정 버튼보다 앞이라 채널 버튼에 착지한다 — TV 에서 원하는 그 자리다.
 */
export function entryFocusIndex(candidates: readonly FocusCandidate[]): number | null {
  let first: number | null = null;
  for (const candidate of candidates) {
    if (!isDrawn(candidate)) continue;
    if (first === null || candidate.index < first) first = candidate.index;
  }
  return first;
}
